/**
 * PATIENT-360-ENTERPRISE-FINISH-V1 -- the Patient 360 record-page REGIONS.
 *
 * Page-local PRESENTATION ONLY. Every region here renders facts the page
 * ALREADY received from the two existing reads (the record read + the
 * operational summary read). Nothing in this file fetches, mutates, derives
 * a new fact, or widens a read: the server SELECT fences remain the
 * authority (CLINICAL WIRE LAW, CLAUDE.md "Clinical visibility").
 *
 * PRIMITIVE ADOPTION (V7-P4 reuse gate -- adopt, never mint a competitor):
 * - DataTable        -- the related-list idiom (registry component.generic-
 *                       table); wraps its own overflow-x container, so a
 *                       wide list never scrolls the PAGE horizontally.
 * - RelatedHistory   -- the related-RECORDS list with per-row provenance
 *                       (actor + zone-labeled time) and a built-in honest
 *                       empty state.
 * - StatusChip       -- THE appointment status canon. Adopted ONLY because
 *                       its labels and tones are byte-identical to the
 *                       page's existing APPOINTMENT_STATUS_LABELS +
 *                       STATUS_TONE maps (pinned in the package test), so
 *                       the status vocabulary does not move.
 * - EmptyState / LoadingState / ErrorState -- the 03 P11 honest-state trio.
 *
 * NOT ADOPTED, ON PURPOSE (recorded in the run record):
 * - StatusChip's PAYMENT canon: its vocabulary
 *   (none / collect_pending / advised_only_no_handoff / unknown_on_this_read)
 *   is NOT the Patient 360 payment-evidence vocabulary
 *   (recorded / multiple_recorded / none_recorded / unavailable). Adopting it
 *   would MOVE the status vocabulary, which this package forbids; the page
 *   keeps PAYMENT_STATE_LABELS.
 * - A timeline RENDERER: registry component.condensed-timeline carries the
 *   explicit dontRule "a fourth timeline renderer", and its unification is
 *   the V7-P4 / gap.r-40 carrier's work. The activity lane therefore adopts
 *   RelatedHistory (rows in CALLER order -- ordering is the caller's duty
 *   per that primitive's contract) instead of minting a timeline.
 */

import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';
import { clinicLabelOf } from '@/dashboard/clinic-context';
import {
  AcBadge,
  AcButton,
  DataTable,
  EmptyState,
  LoadingState,
  RelatedHistory,
  StatusChip,
  type DataTableColumn,
  type RelatedHistoryItem,
  type RoleLensAudience,
} from '@/dashboard/ui';
import {
  ACTIVITY_EMPTY_BODY,
  ACTIVITY_EMPTY_TITLE,
  appointmentDoctorQueueLabel,
  formatPaymentAmount,
  intakeEvidenceLabel,
  buildCourseMoneyTotal,
  PAYMENT_UNLINKED_COPY,
  type PatientCourseMoneyLine,
  type Patient360AppointmentWire,
  type Patient360PaymentItemWire,
  type PatientActivityEntry,
  VISIT_CLINIC_LABEL,
} from '@/dashboard/patients/patient-360-view';

/** The audience prop shape shared by the role-shaped regions. The page
 *  resolves it from `me.role` (server truth); an unknown/absent value fails
 *  CLOSED inside the primitives, exactly as they specify. */
export interface RegionAudienceProps {
  readonly audience: RoleLensAudience | string | null | undefined;
}

// --- Honest states (D4) -----------------------------------------------------

/**
 * The per-region loading skeleton. A region whose lane is still in flight
 * renders a DESIGNED skeleton -- never a blank hole, and never the empty
 * state (an empty state would assert "there is nothing", which is a lie
 * while the read is still running).
 */
export function RegionSkeleton({
  label,
  lines = 2,
  testId,
}: {
  label: string;
  lines?: number;
  testId: string;
}): JSX.Element {
  return <LoadingState label={label} lines={lines} testId={testId} />;
}

// --- Appointments related list (D3, CURRENT side) ---------------------------

/** The linkage fact, as a plain typed string (FIELDS, NOT SENTENCES). */
function linkageLabel(a: Patient360AppointmentWire): string {
  return a.linkage === 'direct'
    ? 'Linked to this patient'
    : 'Via lead - not directly linked';
}

const APPOINTMENT_COLUMNS: ReadonlyArray<
  DataTableColumn<Patient360AppointmentWire>
> = [
  {
    key: 'when',
    header: 'Scheduled (IST)',
    render: (a) => formatTimestampIst(a.scheduledAt),
  },
  {
    key: 'status',
    header: 'Status',
    render: (a) => (
      <StatusChip
        domain="appointment"
        status={a.status}
        testId="patient360-appointment-status"
      />
    ),
  },
  {
    key: 'clinic',
    // Each row is ONE APPOINTMENT, so this column is the branch THAT EPISODE
    // happened at -- not a property of the patient. The bare word "Clinic"
    // read as the second, and the header above it already answers a different
    // question ("Registered at").
    header: VISIT_CLINIC_LABEL,
    render: (a) => clinicLabelOf(a.clinicLabel),
  },
  {
    key: 'doctor',
    header: 'Doctor',
    render: (a) => (
      <span data-testid="patient360-appointment-doctor">
        {a.doctorName ?? 'Not assigned'}
      </span>
    ),
  },
  {
    key: 'queue',
    header: 'Doctor queue',
    render: (a) => appointmentDoctorQueueLabel(a),
  },
  {
    key: 'intake',
    header: 'Intake',
    render: (a) => intakeEvidenceLabel(a.intake),
  },
  { key: 'linkage', header: 'Appointment link', render: linkageLabel },
];

/**
 * The CURRENT appointment related list: the today rows that are NOT the
 * focus card, plus every upcoming appointment. The focus appointment keeps
 * its own detailed card on the page (P10: CARD when a single object gets
 * full attention; TABLE for many homogeneous rows) -- so no appointment is
 * rendered twice.
 */
export function PatientAppointmentsTable({
  rows,
  emptyTitle,
  testId,
}: {
  rows: readonly Patient360AppointmentWire[];
  /** Omit when the CALLER already renders the region's honest empty copy --
   *  passing one anyway would render the same fact twice. */
  emptyTitle?: string;
  testId: string;
}): JSX.Element | null {
  return (
    <DataTable
      columns={APPOINTMENT_COLUMNS}
      rows={rows}
      rowKey={(a) => a.appointmentId}
      label="Appointments"
      testId={testId}
      empty={
        emptyTitle === undefined ? undefined : (
          <EmptyState title={emptyTitle} testId={`${testId}-empty`} />
        )
      }
    />
  );
}

// --- Payments related list (D3, CURRENT side) -------------------------------

const PAYMENT_COLUMNS: ReadonlyArray<DataTableColumn<Patient360PaymentItemWire>> =
  [
    {
      key: 'amount',
      header: 'Amount',
      align: 'end',
      render: (p) => (
        <span data-testid="patient360-payment-amount">
          {formatPaymentAmount(p.amountMinorUnits, p.currency)}
        </span>
      ),
    },
    {
      key: 'when',
      header: 'Recorded (IST)',
      render: (p) => formatTimestampIst(p.occurredAt),
    },
    {
      key: 'linkage',
      header: 'Appointment link',
      render: (p) =>
        p.appointmentId === null
          ? PAYMENT_UNLINKED_COPY
          : `Appointment #${p.appointmentId}`,
    },
  ];

/**
 * The ACTIVE collection lane as a condensed table. Evidence rows ONLY --
 * this region never totals, nets, or computes a balance (the page's
 * PAYMENT_BALANCE_COPY stands as the account line).
 */
export function PatientPaymentsTable({
  rows,
  emptyTitle,
  testId,
}: {
  rows: readonly Patient360PaymentItemWire[];
  /** Omit when the CALLER already renders the region's honest empty copy --
   *  the payment-evidence STATE line is exactly that, so passing one here
   *  would render the same fact twice. */
  emptyTitle?: string;
  testId: string;
}): JSX.Element | null {
  return (
    <DataTable
      columns={PAYMENT_COLUMNS}
      rows={rows}
      rowKey={(p) => p.paymentId}
      label="Recorded payments"
      testId={testId}
      empty={
        emptyTitle === undefined ? undefined : (
          <EmptyState title={emptyTitle} testId={`${testId}-empty`} />
        )
      }
    />
  );
}

// --- Activity lane (D2, HISTORY side) ---------------------------------------

/**
 * Map the pure activity entries onto the RelatedHistory row contract. The
 * zone-labeled timestamp is formatted HERE (the primitive formats nothing).
 * The consult attribution travels as a labeled FACT rather than the
 * provenance actor line, so the banked "Consult by X" / "Unattributed" copy
 * stays byte-identical and a visit with no consult act carries no
 * attribution at all.
 */
export function activityRows(
  entries: readonly PatientActivityEntry[],
): readonly RelatedHistoryItem[] {
  return entries.map((entry) => ({
    id: entry.id,
    title: entry.title,
    facts: entry.facts,
    ...(entry.appointmentStatus !== null
      ? {
          status: {
            domain: 'appointment' as const,
            status: entry.appointmentStatus,
          },
        }
      : {}),
    provenance: { atLabel: formatTimestampIst(entry.occurredAt) },
  }));
}

/**
 * The ONE chronological activity strip (newest first, ordered by the pure
 * builder). Fails CLOSED on an unknown audience via the primitive.
 */
export function PatientActivityLane({
  audience,
  entries,
  testId,
}: RegionAudienceProps & {
  entries: readonly PatientActivityEntry[];
  testId: string;
}): JSX.Element {
  return (
    <RelatedHistory
      audience={audience}
      items={activityRows(entries)}
      emptyTitle={ACTIVITY_EMPTY_TITLE}
      emptyBody={ACTIVITY_EMPTY_BODY}
      testId={testId}
    />
  );
}

// --- Record-detail list regions (D4 honest empties) -------------------------

/**
 * The collapsed record-detail lists previously rendered as bare muted
 * paragraphs on empty. Each now carries its OWN designed empty copy
 * (03 P11: never a generic "no data", never a blank hole).
 */
export function RecordDetailList<Item>({
  items,
  emptyTitle,
  emptyTestId,
  listClassName = 'space-y-1 text-sm',
  renderItem,
  itemKey,
}: {
  items: readonly Item[];
  emptyTitle: string;
  emptyTestId: string;
  /** The EXISTING utility classes for this list (no new CSS is authored by
   *  this package -- the shared token/CSS layer is owned by R-40). */
  listClassName?: string;
  renderItem: (item: Item, index: number) => JSX.Element;
  itemKey: (item: Item, index: number) => string;
}): JSX.Element {
  if (items.length === 0) {
    return <EmptyState title={emptyTitle} testId={emptyTestId} />;
  }
  return (
    <ul className={listClassName}>
      {items.map((item, index) => (
        <li key={itemKey(item, index)}>{renderItem(item, index)}</li>
      ))}
    </ul>
  );
}

// --- PATIENT-360-DESIGN-CONSISTENCY-V1 (founder MATCH 2026-08-17) ----------

/**
 * MONEY BY TREATMENT COURSE (P2): one row per course, read straight off the
 * course roll-up the wire already carries. The across-all-courses line rides
 * the table's OWN disclosed count line -- the primitive's existing slot for
 * "the cut is always disclosed", so no bespoke total strip is minted.
 *
 * ADOPTS DataTable (registry component.generic-table): it already owns the
 * hairline/micro-label/overflow-x idiom, so a course lane never scrolls the
 * PAGE. No new CSS, no second table family.
 */
const COURSE_MONEY_COLUMNS: ReadonlyArray<DataTableColumn<PatientCourseMoneyLine>> =
  [
    {
      key: 'course',
      header: 'Course',
      render: (line) => (
        <span data-testid={`patient360-course-money-line-${line.courseId}`}>
          {line.courseLabel}
        </span>
      ),
    },
    {
      key: 'due',
      header: 'Due',
      align: 'end',
      // COLOUR IS NEVER THE ONLY CHANNEL (P6): the tone decorates a chip that
      // always carries the WORDS of the state it means.
      render: (line) => <AcBadge tone={line.dueTone}>{line.dueLabel}</AcBadge>,
    },
    {
      key: 'collected',
      header: 'Collected',
      align: 'end',
      render: (line) => line.collectedLabel,
    },
  ];

export function PatientCourseMoneyTable({
  lines,
  testId,
}: {
  readonly lines: readonly PatientCourseMoneyLine[];
  readonly testId: string;
}): JSX.Element | null {
  if (lines.length === 0) return null;
  const total = buildCourseMoneyTotal(lines);
  return (
    <DataTable
      columns={COURSE_MONEY_COLUMNS}
      rows={lines}
      rowKey={(line) => line.courseId}
      label="Money by treatment course"
      testId={testId}
      countLine={
        <span data-testid={`${testId}-total`}>
          {/* CONTRACT 9a: the roll-up states the weaker true claim whenever
              an obligation's size is unknown -- never "fully settled". */}
          {total.line}
        </span>
      }
    />
  );
}

/**
 * THE ONE capped-history footer (P3 / contract 8a): the honest cap line
 * ("Showing 3 of 25") beside the door that opens the WHOLE history in the
 * shared DrawerShell. One grammar for every historical list on this carrier,
 * so a second "load more" pattern can never appear beside it.
 *
 * Renders NOTHING when nothing was withheld -- an uncapped list never claims
 * a cap, and a door that opens the same rows twice is not a door.
 */
export function CappedHistoryFooter({
  capLine,
  doorLabel,
  onOpen,
  testId,
  capInHeader = false,
}: {
  readonly capLine: string | null;
  readonly doorLabel: string;
  readonly onOpen: () => void;
  readonly testId: string;
  /** CONTRACT s.8/s.8a: when the SECTION HEADER already states the cap, the
   *  footer carries the door alone -- the same fact twice is noise, and the
   *  header is where the contract puts it. */
  readonly capInHeader?: boolean;
}): JSX.Element | null {
  if (capLine === null) return null;
  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-2"
      data-testid={`${testId}-foot`}
    >
      {capInHeader ? null : (
        <>
          <span
            className="text-xs text-muted-foreground"
            data-testid={`${testId}-cap`}
          >
            {capLine}
          </span>{' '}
        </>
      )}
      <AcButton
        variant="ghost"
        size="sm"
        onClick={onOpen}
        data-testid={`${testId}-full-history`}
      >
        {doorLabel}
      </AcButton>
    </div>
  );
}
